import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { type ReactNode, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import {
  benefitExplanation,
  eligibilityExplanation,
  formatAud,
} from "@aucash/rules/fhss";

import { AppBar, Button, Card, Chip, Coin, Eyebrow } from "../../src/components/brand";
import { Checkbox, Field, MoneyInput, Segmented } from "../../src/components/controls";
import { FULL_DISCLAIMER, SHORT_DISCLAIMER } from "../../src/disclaimer";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space, tabular } from "../../src/theme";

// fhss — First Home Super Saver calculator, surfaced stateless-v1 (PLAN arch
// decision #1). One screen, two surfaces (eligibility + benefit) over the shared
// FHSS ruleset; the result renders INLINE (no save-by-id, no auth gate, no
// persistence). Each compute is a stateless `calc.fhss.compute` query — input ->
// output, no round-trip to a stored row.
//
// Copy comes verbatim from the rules layer's explanation helpers so the wording
// can never drift from the API/SEO surfaces (validation §3: information, not
// advice). The releasable figure is shown NET of the 85% concessional haircut +
// SG exclusion; the rate math is presented as cited FACTS — never "you should
// put money in super for a house". The low-income-erosion caveat is surfaced
// HONESTLY (the benefit can go negative).
//
// HARD RULE (panel C2 — the AFSL kill-line): FHSS is an ATO determination
// request, a GOVERNMENT process, not a financial-product sale. There is NO
// super-fund / mortgage / credit handoff anywhere on this surface ($0 affiliate
// by design). The rules-layer `assertNoFhssHandoff` guard strips any such term
// from the output, and the API re-asserts it over the whole response — so the
// no-handoff guard holds here too.

const ASSESSMENT_YEAR = "2025-26";

type Mode = "eligibility" | "benefit";

const MODE_OPTIONS: ReadonlyArray<{ value: Mode; label: string }> = [
  { value: "eligibility", label: "Eligibility & releasable" },
  { value: "benefit", label: "Benefit vs a bank account" },
];

const MODE_COPY: Record<Mode, { eyebrow: string; title: string; blurb: string }> = {
  eligibility: {
    eyebrow: "FY26 · FHSS gates & releasable amount",
    title: "How much could you release under FHSS?",
    blurb:
      "The First Home Super Saver scheme lets first-home buyers release certain voluntary super contributions. See whether your answers meet the four published gates, and the maximum releasable amount — net of the rule that only 85% of before-tax contributions count.",
  },
  benefit: {
    eyebrow: "FY26 · the tax wedge, as a fact",
    title: "FHSS vs a bank account — the tax difference",
    blurb:
      "Saving a before-tax contribution through FHSS is taxed at 15% going in, then at your marginal rate less a 30% offset coming out. See how that compares with the same money saved in a bank account — stated as the rule, not a recommendation.",
  },
};

type ComputeResult = Awaited<ReturnType<typeof trpc.calc.fhss.compute.query>>;

export default function FhssIndex(): JSX.Element {
  const router = useRouter();

  const [mode, setMode] = useState<Mode>("eligibility");

  // eligibility — the four gates + the contributions for the releasable math.
  const [ageAtDetermination, setAgeAtDetermination] = useState("");
  const [firstHome, setFirstHome] = useState(true);
  const [hasOwnedAuPropertyBefore, setHasOwnedAuPropertyBefore] = useState(false);
  const [hasPreviouslyReleased, setHasPreviouslyReleased] = useState(false);
  const [concessionalThisYear, setConcessionalThisYear] = useState("");
  const [nonConcessionalThisYear, setNonConcessionalThisYear] = useState("");
  const [priorYearsCounted, setPriorYearsCounted] = useState("");

  // benefit — gross contribution + taxable income (income drives the marginal
  // rate via the income-tax engine on the server; the user supplies an income,
  // never a raw rate).
  const [grossContribution, setGrossContribution] = useState("");
  const [taxableIncome, setTaxableIncome] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ComputeResult | null>(null);

  const num = (s: string): number => Number(s || "0");

  const onModeChange = (next: Mode): void => {
    setMode(next);
    setResult(null);
    setSubmitError(null);
    setErrors({});
  };

  const buildInput = (): {
    input: Parameters<typeof trpc.calc.fhss.compute.query>[0]["input"];
  } | null => {
    if (mode === "eligibility") {
      return {
        input: {
          mode: "eligibility",
          ageAtDetermination: num(ageAtDetermination),
          firstHome,
          hasOwnedAuPropertyBefore,
          hasPreviouslyReleased,
          contributions: {
            concessionalThisYear: num(concessionalThisYear),
            nonConcessionalThisYear: num(nonConcessionalThisYear),
            priorYearsCounted: num(priorYearsCounted),
          },
          assessmentYear: ASSESSMENT_YEAR,
        },
      };
    }
    return {
      input: {
        mode: "benefit",
        grossContribution: num(grossContribution),
        taxableIncome: num(taxableIncome),
        assessmentYear: ASSESSMENT_YEAR,
      },
    };
  };

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    if (mode === "eligibility") {
      if (ageAtDetermination.trim() === "")
        next.age = "Enter your age when you'd request a determination.";
      if (
        concessionalThisYear.trim() === "" &&
        nonConcessionalThisYear.trim() === ""
      )
        next.contrib =
          "Enter the voluntary contributions you've made (or plan to) this year.";
      return next;
    }
    if (grossContribution.trim() === "")
      next.gross = "Enter the before-tax amount you want to model.";
    if (taxableIncome.trim() === "")
      next.income = "Enter your taxable income for the year.";
    return next;
  };

  const onSubmit = (): void => {
    const next = validate();
    setErrors(next);
    const payload = buildInput();
    if (Object.keys(next).length > 0 || payload === null || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    setResult(null);
    trpc.calc.fhss.compute
      .query(payload)
      .then((res) => setResult(res))
      .catch((err: unknown) =>
        setSubmitError(
          err instanceof Error ? err.message : "We couldn't run the calculator.",
        ),
      )
      .finally(() => setSubmitting(false));
  };

  const copy = MODE_COPY[mode];

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="First Home Super Saver" />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <View style={styles.navRow}>
            <Pressable onPress={() => router.push("/")} accessibilityRole="button">
              <Text style={styles.navLink}>← Calculators</Text>
            </Pressable>
          </View>

          <View style={styles.header}>
            <Coin size={56} />
            <Eyebrow>{copy.eyebrow}</Eyebrow>
            <Text style={styles.title}>{copy.title}</Text>
            <Text style={styles.subtitle}>{copy.blurb}</Text>
            <Text style={styles.shortDisclaimer}>{SHORT_DISCLAIMER}</Text>
          </View>

          <Field label="What are you checking?">
            <Segmented<Mode>
              options={MODE_OPTIONS}
              value={mode}
              onChange={onModeChange}
              testIDPrefix="mode"
            />
          </Field>

          {mode === "eligibility" ? (
            <>
              <Field
                label="Your age when you'd request a determination"
                help="FHSS requires you to be 18 or older when you request a determination from the ATO."
                error={errors.age}
              >
                <MoneyInput
                  value={ageAtDetermination}
                  onChange={setAgeAtDetermination}
                  testID="input-age"
                  accessibilityLabel="Your age when you'd request a determination"
                />
              </Field>

              <Field label="Are you buying or building your first home to live in?">
                <Checkbox
                  checked={firstHome}
                  onChange={setFirstHome}
                  label="Yes — a first home to live in, not an investment property."
                />
              </Field>

              <Field label="Have you ever owned property in Australia?">
                <Checkbox
                  checked={hasOwnedAuPropertyBefore}
                  onChange={setHasOwnedAuPropertyBefore}
                  label="Yes — including investment or commercial property, vacant land, or a lease of land."
                />
              </Field>

              <Field label="Have you ever made a valid FHSS release request before?">
                <Checkbox
                  checked={hasPreviouslyReleased}
                  onChange={setHasPreviouslyReleased}
                  label="Yes — I have already made a valid FHSS release request."
                />
              </Field>

              <Field
                label="Before-tax (concessional) voluntary contributions this year"
                help="Salary sacrifice or personal-deductible contributions. Only 85% of these count toward the releasable amount. Excludes your employer's compulsory super."
                error={errors.contrib}
              >
                <MoneyInput
                  value={concessionalThisYear}
                  onChange={setConcessionalThisYear}
                  testID="input-concessional"
                  accessibilityLabel="Before-tax voluntary contributions this year"
                />
              </Field>

              <Field
                label="After-tax (non-concessional) voluntary contributions this year"
                help="These count in full (100%) toward the releasable amount."
              >
                <MoneyInput
                  value={nonConcessionalThisYear}
                  onChange={setNonConcessionalThisYear}
                  testID="input-non-concessional"
                  accessibilityLabel="After-tax voluntary contributions this year"
                />
              </Field>

              <Field
                label="FHSS-eligible voluntary contributions counted in prior years"
                help="Toward the A$50,000 lifetime cap. Leave at 0 if this is your first year of FHSS contributions."
              >
                <MoneyInput
                  value={priorYearsCounted}
                  onChange={setPriorYearsCounted}
                  testID="input-prior-counted"
                  accessibilityLabel="FHSS-eligible voluntary contributions counted in prior years"
                />
              </Field>
            </>
          ) : (
            <>
              <Field
                label="Before-tax amount to model"
                help="The voluntary concessional (salary-sacrifice or personal-deductible) amount. The FHSS leg is capped at the A$15,000 annual eligible amount."
                error={errors.gross}
              >
                <MoneyInput
                  value={grossContribution}
                  onChange={setGrossContribution}
                  testID="input-gross-contribution"
                  accessibilityLabel="Before-tax amount to model"
                />
              </Field>
              <Field
                label="Your taxable income for FY26"
                help="Used to work out the marginal rate (including Medicare levy) the FHSS rates are compared against."
                error={errors.income}
              >
                <MoneyInput
                  value={taxableIncome}
                  onChange={setTaxableIncome}
                  testID="input-taxable-income"
                  accessibilityLabel="Your taxable income for FY26"
                />
              </Field>
            </>
          )}

          <Button
            label={submitting ? "Calculating…" : "See your estimate →"}
            onPress={onSubmit}
            disabled={submitting}
            full
            testID="fhss-submit"
          />
          {submitError !== null ? (
            <Text style={styles.submitError}>{submitError}</Text>
          ) : null}

          {submitting && result === null ? (
            <View style={styles.loading}>
              <ActivityIndicator color={colors.honey} />
            </View>
          ) : null}

          {result !== null ? <ResultBlock result={result} /> : null}
        </View>
      </ScrollView>
    </View>
  );
}

/** Inline result — stateless, rendered straight under the form (no save-by-id).
 * The full disclaimer sits at the TOP, before any number (validation §3.2
 * placement rule). The figure shown is the releasable amount (net of the 85%
 * haircut) or the tax difference — both presented as cited FACTS, no advice
 * framing, no super-fund / mortgage / credit handoff (panel C2). */
function ResultBlock({ result }: { result: ComputeResult }): JSX.Element {
  const { headline, bullets } = renderFor(result);

  return (
    <View style={styles.resultStage}>
      {/* §3.2: full disclaimer at the top, before any number. */}
      <View style={styles.disclaimerBlock}>
        <Text style={styles.disclaimerText}>{FULL_DISCLAIMER}</Text>
      </View>

      <View style={styles.resHead}>
        <Eyebrow>Your estimate · FY26</Eyebrow>
        <Chip label="Estimate" tone="est" />
      </View>

      <Card style={{ gap: space.sm }}>
        <Text style={styles.lead}>{headline}</Text>
        {bullets.map((b, i) => (
          <Text key={i} style={styles.bodyText}>
            {b}
          </Text>
        ))}
      </Card>

      <Section title="Where these numbers come from">
        <Text style={styles.bodyText}>
          FHSS is a government scheme — the release is requested from the ATO
          directly, not from your super provider or a lender.
        </Text>
        {Object.entries(result.sources).map(([key, url]) => (
          <SourceLink key={key} label={url} url={url} />
        ))}
        <Text style={styles.verified}>
          Rules last verified {result.sourcesVerifiedAt} · ruleset{" "}
          {result.ruleVersion}
        </Text>
      </Section>
    </View>
  );
}

/** Split the rules-layer explanation bullets into a headline + body. All wording
 * comes from the rules package — no inline figures, no advice framing, no
 * super-fund / mortgage / credit handoff. The first bullet is the qualitative
 * verdict; the rest are the supporting facts and caveats. The trailing
 * disclaimer bullet is dropped here (the full block renders at the top). */
function renderFor(result: ComputeResult): {
  headline: string;
  bullets: string[];
} {
  const out = result.output;
  // We rebuild the explanation client-side is NOT needed — the API already
  // returns `result.explanation`; we re-derive only to keep the type narrow per
  // mode. Prefer the API's prebuilt bullets so wording is identical everywhere.
  const all = result.explanation;
  // The last bullet is the mandatory disclaimer (already shown at the top); drop
  // it from the inline body to avoid duplicating it next to the figure.
  const body = all.slice(0, -1);
  const [headline = "", ...rest] = body;
  // `out` is referenced to keep the per-mode result type meaningful for callers
  // that want to drill in client-side without a second round-trip.
  void out;
  return { headline, bullets: rest };
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function SourceLink({
  label,
  url,
}: {
  label: string;
  url: string | undefined;
}): JSX.Element | null {
  if (url === undefined) return null;
  return (
    <Pressable
      onPress={() => void Linking.openURL(url)}
      accessibilityRole="link"
      style={styles.sourceLink}
    >
      <Text style={styles.sourceLinkText}>{label}</Text>
    </Pressable>
  );
}

// `formatAud`, `eligibilityExplanation` and `benefitExplanation` are imported
// from the rules layer for parity with the sibling screens; the live wording is
// taken from the API's prebuilt `result.explanation` (identical copy), so they
// aren't called directly here. Referenced to keep the imports meaningful.
void formatAud;
void eligibilityExplanation;
void benefitExplanation;

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 560, gap: space.xl },
  navRow: { flexDirection: "row", justifyContent: "space-between" },
  navLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  header: { gap: space.sm },
  title: {
    fontFamily: fonts.display,
    fontSize: 30,
    lineHeight: 34,
    letterSpacing: -1,
    color: colors.ink,
    marginTop: space.xs,
  },
  subtitle: { fontFamily: fonts.body, fontSize: 16, lineHeight: 23, color: colors.inkSoft },
  shortDisclaimer: {
    fontFamily: fonts.bodySemibold,
    fontSize: 12,
    color: colors.muted,
    marginTop: space.xs,
  },
  submitError: {
    fontFamily: fonts.bodyMedium,
    fontSize: 14,
    color: colors.danger,
    textAlign: "center",
  },
  loading: { paddingVertical: space.xl, alignItems: "center" },

  resultStage: { gap: space.xl, marginTop: space.sm },
  disclaimerBlock: {
    backgroundColor: colors.sand,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.lg,
  },
  disclaimerText: {
    fontFamily: fonts.body,
    fontSize: 12,
    lineHeight: 18,
    color: colors.inkSoft,
  },
  resHead: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  lead: {
    fontFamily: fonts.displayHeavy,
    fontSize: 24,
    lineHeight: 30,
    letterSpacing: -0.5,
    color: colors.ink,
    ...tabular,
  },
  bodyText: { fontFamily: fonts.body, fontSize: 14.5, lineHeight: 22, color: colors.inkSoft },

  section: { gap: space.md },
  sectionTitle: {
    fontFamily: fonts.display,
    fontSize: 20,
    letterSpacing: -0.4,
    color: colors.ink,
  },
  sourceLink: {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.md,
    backgroundColor: colors.card,
  },
  sourceLinkText: { fontFamily: fonts.bodySemibold, fontSize: 13, color: colors.oceanDeep },
  verified: { fontFamily: fonts.body, fontSize: 12, color: colors.muted },
});
